import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { pageCache } from '../utils/pageCache';
import {
  Sparkles, BarChart3,
  Zap, Flag, ChevronRight, Target, Plus, Trash2,
  Printer, Share2, AlertCircle, GitCompare, X, Check,
} from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, ReferenceDot, Legend,
} from 'recharts';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { fmt, fmtK, fmtPct, fmtCount, changeDirection } from '../utils/formatters';
import { getCategoryStyle } from '../utils/colors';
import LoadingScreen from '../components/LoadingScreen';
import DataEndsNote from '../components/DataEndsNote';
import Change from '../components/Change';

function KpiCard({ label, value, change, prefix = '$', suffix = '', changeUnit = '%', sparkData }: {
  label: string; value: number; change: number | null; prefix?: string; suffix?: string; changeUnit?: '%' | 'pts'; sparkData?: number[];
}) {
  // The sparkline takes the colour its change line shows: neutral when there is
  // no honest change (null) or it rounds to 0%, never the colour of a rise.
  const dir = change == null ? 'flat' : changeDirection(change);
  const sparkStroke = dir === 'up' ? '#2E6B4F' : dir === 'down' ? '#B54A4A' : '#6B6670';
  return (
    <div className="bg-white border border-[#E6E2DE] rounded-[14px] p-6 shadow-card hover:-translate-y-1 hover:shadow-[0_8px_24px_rgba(74,45,94,0.08)] transition-all duration-300">
      <div className="text-xs font-sans font-medium text-[#6B6670] uppercase tracking-wide mb-1">{label}</div>
      <div className="text-2xl font-sans font-bold text-[#2D2A2E] tabular-nums">
        {prefix === '$' ? fmt(value) : suffix === '%' ? fmtPct(value) : fmtCount(value)}
      </div>
      {/* null: this period and the last count different units (orders vs items), so there is no honest change to show. */}
      {change != null && (
        <div className="flex items-center gap-1 mt-1">
          <Change value={change} unit={changeUnit} />
          <span className="text-[#6B6670] text-xs font-sans ml-1">vs prior period</span>
        </div>
      )}
      {sparkData && sparkData.length > 2 && (
        <div className="mt-3 -mx-1">
          <ResponsiveContainer width="100%" height={32}>
            <LineChart data={sparkData.map((v, i) => ({ v, i }))}>
              <Line type="monotone" dataKey="v" stroke={sparkStroke} strokeWidth={1.5} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

function GoalModal({ onClose, onSave, period, countLabel = 'Transactions' }: { onClose: () => void; onSave: (g: any) => void; period: string; countLabel?: string }) {
  const [form, setForm] = useState({ label: '', target: '', metric: 'revenue', period });
  const [saving, setSaving] = useState(false);
  const handle = async () => {
    if (!form.label || !form.target) return;
    setSaving(true);
    try {
      const res = await axios.post('/api/goals', { ...form, target: parseFloat(form.target) });
      onSave(res.data);
      onClose();
      toast.success('Goal created');
    } catch { toast.error('Failed to create goal'); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-[16px] p-6 w-full max-w-sm shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-lg text-[#2D2A2E]">New Goal</h3>
          <button onClick={onClose}><X size={18} strokeWidth={1.5} className="text-[#6B6670]" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-sans font-medium text-[#6B6670] uppercase tracking-wide mb-1">Goal Name</label>
            <input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="e.g. Hit $5K this month" className="w-full border border-[#E6E2DE] rounded-[8px] h-10 px-3 font-sans text-sm outline-none focus:ring-2 focus:ring-[#4A2D5E]/20 focus:border-[#4A2D5E]" />
          </div>
          <div>
            <label className="block text-xs font-sans font-medium text-[#6B6670] uppercase tracking-wide mb-1">Metric</label>
            <select value={form.metric} onChange={(e) => setForm({ ...form, metric: e.target.value })} className="w-full border border-[#E6E2DE] rounded-[8px] h-10 px-3 font-sans text-sm outline-none focus:ring-2 focus:ring-[#4A2D5E]/20 focus:border-[#4A2D5E]">
              <option value="revenue">Revenue ($)</option>
              <option value="profit">Profit ($)</option>
              <option value="transactions">{countLabel} (#)</option>
              <option value="units">Units Sold (#)</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-sans font-medium text-[#6B6670] uppercase tracking-wide mb-1">Target Value</label>
            <input type="number" value={form.target} onChange={(e) => setForm({ ...form, target: e.target.value })} placeholder="5000" className="w-full border border-[#E6E2DE] rounded-[8px] h-10 px-3 font-sans text-sm outline-none focus:ring-2 focus:ring-[#4A2D5E]/20 focus:border-[#4A2D5E]" />
          </div>
        </div>
        <div className="flex gap-2 mt-5">
          <button onClick={onClose} className="flex-1 border border-[#E6E2DE] rounded-[8px] py-2 font-sans text-sm text-[#6B6670] hover:bg-[#FAF9F7]">Cancel</button>
          <button onClick={handle} disabled={saving} className="flex-1 bg-[#4A2D5E] text-white rounded-[8px] py-2 font-sans text-sm font-medium hover:bg-[#3D254D] disabled:opacity-50">{saving ? 'Saving…' : 'Create Goal'}</button>
        </div>
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const [data, setData] = useState<any>(() => pageCache.get('/api/dashboard?period=month') ?? null);
  const [loading, setLoading] = useState(() => !pageCache.has('/api/dashboard?period=month'));
  const [period, setPeriod] = useState('month');
  const [compareMode, setCompareMode] = useState(false);
  const [showGoalModal, setShowGoalModal] = useState(false);
  const [shareLink, setShareLink] = useState('');
  const [sharingLoading, setSharingLoading] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    const url = `/api/dashboard?period=${period}`;
    if (pageCache.has(url)) {
      setData(pageCache.get(url));
      setLoading(false);
    }
    axios.get(url)
      .then((res) => { pageCache.set(url, res.data); setData(res.data); })
      .catch(() => { if (!pageCache.has(url)) { setData(null); toast.error('Couldn\'t load your dashboard. Check your connection and refresh the page.'); } })
      .finally(() => setLoading(false));
  }, [period]);

  const hasData = data && data.kpis && data.kpis.revenue.value > 0;
  const sparkRevenue = data?.trend?.map((t: any) => t.revenue) || [];
  const sparkTxns = data?.trend?.map((t: any) => t.transactions) || [];

  // Merge current trend with prior revenue for comparison chart
  const comparisonData = data?.trend?.map((t: any, i: number) => ({
    ...t,
    revenue_prior: data.prior_trend?.[i]?.revenue ?? null,
  })) || [];

  const handleShare = async () => {
    setSharingLoading(true);
    try {
      const res = await axios.post('/api/share/create');
      const link = `${window.location.origin}/share/${res.data.token}`;
      setShareLink(link);
      await navigator.clipboard.writeText(link);
      toast.success('Share link copied to clipboard! Expires in 7 days.');
    } catch { toast.error('Failed to create share link'); }
    finally { setSharingLoading(false); }
  };

  const handleDeleteGoal = async (goalId: string) => {
    try {
      await axios.delete(`/api/goals/${goalId}`);
      setData((prev: any) => ({
        ...prev,
        goal_progress: prev.goal_progress.filter((g: any) => g.id !== goalId),
      }));
      toast.success('Goal deleted');
    } catch { toast.error('Failed to delete goal'); }
  };

  const handleGoalSaved = (newGoal: any) => {
    // Refresh data to pick up new goal progress
    axios.get(`/api/dashboard?period=${period}`)
      .then((res) => { pageCache.set(`/api/dashboard?period=${period}`, res.data); setData(res.data); });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
    >
      {showGoalModal && (
        <GoalModal
          onClose={() => setShowGoalModal(false)}
          onSave={handleGoalSaved}
          period={period}
          countLabel={data?.kpis?.transactions?.label}
        />
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-serif text-2xl text-[#2D2A2E]">
            {user?.business_name ? `${user.business_name} Dashboard` : 'Dashboard'}
          </h1>
          <DataEndsNote dataEnds={data?.data_ends} period={period} />
          {data?.notice && <p className="font-sans text-xs text-[#4A2D5E] mt-1.5 max-w-xl">{data.notice}</p>}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Comparison Mode Toggle */}
          <button
            onClick={() => setCompareMode(!compareMode)}
            className={`no-print inline-flex items-center gap-1.5 h-10 px-3 rounded-[8px] font-sans text-sm border transition-all ${compareMode ? 'bg-[#4A2D5E] text-white border-[#4A2D5E]' : 'bg-white text-[#6B6670] border-[#E6E2DE] hover:border-[#4A2D5E]'}`}
          >
            <GitCompare size={15} strokeWidth={1.5} />
            Compare
          </button>
          {/* Export / Print */}
          <button
            onClick={() => window.print()}
            className="no-print inline-flex items-center gap-1.5 h-10 px-3 rounded-[8px] font-sans text-sm bg-white border border-[#E6E2DE] text-[#6B6670] hover:border-[#4A2D5E] transition-all"
          >
            <Printer size={15} strokeWidth={1.5} />
            Export
          </button>
          {/* Share */}
          <button
            onClick={handleShare}
            disabled={sharingLoading}
            className="no-print inline-flex items-center gap-1.5 h-10 px-3 rounded-[8px] font-sans text-sm bg-white border border-[#E6E2DE] text-[#6B6670] hover:border-[#4A2D5E] transition-all disabled:opacity-50"
          >
            <Share2 size={15} strokeWidth={1.5} />
            {sharingLoading ? 'Creating…' : 'Share'}
          </button>
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="bg-white border border-[#E6E2DE] focus:ring-2 focus:ring-[#4A2D5E]/20 focus:border-[#4A2D5E] rounded-[8px] h-10 font-sans text-sm px-3 outline-none w-44"
          >
            <option value="week">This Week</option>
            <option value="month">This Month</option>
            <option value="quarter">Last 90 Days</option>
          </select>
        </div>
      </div>

      {loading ? (
        <LoadingScreen fullScreen={false} />
      ) : !hasData ? (
        <div className="flex flex-col items-center justify-center py-24">
          <BarChart3 size={48} strokeWidth={1.5} className="text-[#4A2D5E]/30 mb-4" />
          <h3 className="font-serif text-xl text-[#2D2A2E] mb-2">No data yet</h3>
          <p className="font-sans text-sm text-[#6B6670] mb-6 text-center max-w-sm">
            Upload your POS transaction data to see your dashboard come to life.
          </p>
          <button
            onClick={() => navigate('/settings/data')}
            className="bg-[#2E6B4F] text-white hover:bg-[#245A41] rounded-[8px] shadow-sm font-sans font-medium px-6 py-2.5 transition-colors"
          >
            Upload Data
          </button>
        </div>
      ) : (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <KpiCard label="Revenue" value={data.kpis.revenue.value} change={data.kpis.revenue.change} sparkData={sparkRevenue} />
            <KpiCard label="Gross profit (after COGS)" value={data.kpis.profit.value} change={data.kpis.profit.change} sparkData={sparkRevenue.map((r: number) => r * (data.kpis.margin.value / 100))} />
            <KpiCard label="Margin" value={data.kpis.margin.value} change={data.kpis.margin.change} prefix="" suffix="%" changeUnit="pts" />
            <KpiCard label={data.kpis.transactions.label || 'Transactions'} value={data.kpis.transactions.value} change={data.kpis.transactions.change} prefix="" sparkData={sparkTxns} />
          </div>

          {/* Goal Tracker — always render when the key is present so the empty
              state (and its "Add Goal" button) is reachable with zero goals. */}
          {data.goal_progress && (
            <div className="bg-white border border-[#E6E2DE] rounded-[14px] p-6 shadow-card mb-8">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Target size={18} strokeWidth={1.5} className="text-[#4A2D5E]" />
                  <h3 className="font-serif text-base text-[#2D2A2E]">Goal Tracker</h3>
                </div>
                <button
                  onClick={() => setShowGoalModal(true)}
                  className="no-print inline-flex items-center gap-1 text-xs font-sans font-medium text-[#4A2D5E] hover:underline"
                >
                  <Plus size={13} strokeWidth={2} /> Add Goal
                </button>
              </div>
              {data.goal_progress.length === 0 ? (
                <div className="text-center py-4">
                  <p className="font-sans text-sm text-[#6B6670]">No goals set for this period.</p>
                  <button onClick={() => setShowGoalModal(true)} className="no-print mt-2 font-sans text-sm text-[#4A2D5E] font-medium hover:underline">
                    Set your first goal →
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {data.goal_progress.map((g: any) => (
                    <div key={g.id}>
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center gap-2">
                          {g.hit ? (
                            <Check size={14} strokeWidth={2} className="text-[#2E6B4F]" />
                          ) : (
                            <Target size={14} strokeWidth={1.5} className="text-[#4A2D5E]" />
                          )}
                          <span className="font-sans text-sm text-[#2D2A2E] font-medium">{g.label}</span>
                          {g.hit && (
                            <span className="text-[10px] font-sans font-medium px-1.5 py-0.5 rounded-full bg-[#2E6B4F]/10 text-[#2E6B4F]">Hit!</span>
                          )}
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="font-sans text-xs text-[#6B6670] tabular-nums">
                            {g.metric === 'revenue' || g.metric === 'profit' ? fmt(g.current) : fmtCount(g.current)} / {g.metric === 'revenue' || g.metric === 'profit' ? fmt(g.target) : fmtCount(g.target)}
                          </span>
                          <button onClick={() => handleDeleteGoal(g.id)} className="no-print text-[#6B6670] hover:text-[#B54A4A]">
                            <Trash2 size={13} strokeWidth={1.5} />
                          </button>
                        </div>
                      </div>
                      <div className="w-full bg-[#F2F0ED] rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all duration-500 ${g.hit ? 'bg-[#2E6B4F]' : 'bg-[#4A2D5E]'}`}
                          style={{ width: `${g.progress}%` }}
                        />
                      </div>
                      <div className="flex justify-between mt-0.5">
                        <span className="font-sans text-[10px] text-[#6B6670]">0</span>
                        <span className="font-sans text-[10px] text-[#4A2D5E] font-medium">{fmtPct(g.progress)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Today's brief teaser */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-5 bg-white border border-[#E6E2DE] rounded-[14px] shadow-card mb-8">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <Sparkles size={18} strokeWidth={1.5} className="text-[#2E6B4F] flex-shrink-0" />
              <div className="min-w-0">
                <h3 className="font-serif text-base text-[#2D2A2E]">Today's brief</h3>
                <p className="font-sans text-sm text-[#6B6670]">Your moves for today are on the Home page.</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/home')}
              className="no-print inline-flex items-center gap-1.5 self-start sm:self-auto bg-[#2E6B4F] text-white hover:bg-[#245A41] rounded-[8px] px-4 py-2 font-sans text-sm font-medium transition-colors whitespace-nowrap"
            >
              Read today's brief <ChevronRight size={14} strokeWidth={2} />
            </button>
          </div>

          {/* Revenue Trend + Top Products */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Revenue Trend Chart */}
            <div className="bg-white border border-[#E6E2DE] rounded-[14px] p-6 shadow-card">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-serif text-lg text-[#2D2A2E]">Revenue Trend</h3>
                {compareMode && (
                  <span className="text-xs font-sans text-[#4A2D5E] bg-[#4A2D5E]/10 px-2 py-0.5 rounded-full">Comparison On</span>
                )}
              </div>
              <ResponsiveContainer width="100%" height={280}>
                {compareMode ? (
                  <AreaChart data={comparisonData}>
                    <defs>
                      <linearGradient id="revenueGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#4A2D5E" stopOpacity={0.12} />
                        <stop offset="95%" stopColor="#4A2D5E" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E6E2DE" />
                    <XAxis dataKey="date" tick={{ fill: '#6B6670', fontSize: 11, fontFamily: 'DM Sans' }} tickFormatter={(v) => v.slice(5)} />
                    <YAxis tick={{ fill: '#6B6670', fontSize: 11, fontFamily: 'DM Sans' }} tickFormatter={(v) => fmtK(v)} />
                    <Tooltip
                      contentStyle={{ borderRadius: '10px', border: '1px solid #E6E2DE', fontFamily: 'DM Sans', fontSize: 12 }}
                      formatter={(value: number, name: string) => [fmt(value), name === 'revenue' ? 'Current' : 'Prior Period']}
                      labelFormatter={(l) => `Date: ${l}`}
                    />
                    <Legend formatter={(v) => v === 'revenue' ? 'Current Period' : 'Prior Period'} />
                    <Area type="monotone" dataKey="revenue" stroke="#4A2D5E" strokeWidth={2} fill="url(#revenueGrad)" />
                    <Area type="monotone" dataKey="revenue_prior" stroke="#7BA68E" strokeWidth={1.5} strokeDasharray="5 3" fill="none" connectNulls />
                    {/* Anomaly dots */}
                    {data.anomalies?.map((a: any) => (
                      <ReferenceDot key={a.date} x={a.date} y={a.revenue} r={5}
                        fill={a.type === 'spike' ? '#2E6B4F' : '#B54A4A'} stroke="white" strokeWidth={1.5}
                        label={{ value: a.type === 'spike' ? '↑' : '↓', position: 'top', fontSize: 10 }}
                      />
                    ))}
                  </AreaChart>
                ) : (
                  <AreaChart data={data.trend}>
                    <defs>
                      <linearGradient id="revenueGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#4A2D5E" stopOpacity={0.1} />
                        <stop offset="95%" stopColor="#4A2D5E" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E6E2DE" />
                    <XAxis dataKey="date" tick={{ fill: '#6B6670', fontSize: 11, fontFamily: 'DM Sans' }} tickFormatter={(v) => v.slice(5)} />
                    <YAxis tick={{ fill: '#6B6670', fontSize: 11, fontFamily: 'DM Sans' }} tickFormatter={(v) => fmtK(v)} />
                    <Tooltip
                      contentStyle={{ borderRadius: '10px', border: '1px solid #E6E2DE', fontFamily: 'DM Sans', fontSize: 12 }}
                      formatter={(value: number) => [fmt(value), 'Revenue']}
                      labelFormatter={(l) => `Date: ${l}`}
                    />
                    <Area type="monotone" dataKey="revenue" stroke="#4A2D5E" strokeWidth={2} fill="url(#revenueGrad)" />
                    {data.anomalies?.map((a: any) => (
                      <ReferenceDot key={a.date} x={a.date} y={a.revenue} r={5}
                        fill={a.type === 'spike' ? '#2E6B4F' : '#B54A4A'} stroke="white" strokeWidth={1.5}
                        label={{ value: a.type === 'spike' ? '↑' : '↓', position: 'top', fontSize: 10 }}
                      />
                    ))}
                  </AreaChart>
                )}
              </ResponsiveContainer>
              {data.anomalies?.length > 0 && (
                <div className="flex items-center gap-3 mt-3 pt-3 border-t border-[#E6E2DE]">
                  <span className="text-[10px] font-sans text-[#6B6670] uppercase tracking-wide">Anomalies:</span>
                  <div className="flex gap-2">
                    <span className="inline-flex items-center gap-1 text-[10px] font-sans text-[#2E6B4F]"><span className="w-2 h-2 rounded-full bg-[#2E6B4F] inline-block" /> Spike</span>
                    <span className="inline-flex items-center gap-1 text-[10px] font-sans text-[#B54A4A]"><span className="w-2 h-2 rounded-full bg-[#B54A4A] inline-block" /> Dip</span>
                  </div>
                </div>
              )}
            </div>

            {/* Top Products Table */}
            <div className="bg-white border border-[#E6E2DE] rounded-[14px] overflow-hidden flex flex-col">
              <div className="p-6 border-b border-[#E6E2DE]">
                <h3 className="font-serif text-lg text-[#2D2A2E]">Top Products</h3>
              </div>
              <div className="overflow-x-auto flex-1">
                <table className="w-full">
                  <thead>
                    <tr className="bg-[#FAF9F7] border-b border-[#E6E2DE]">
                      <th className="text-left p-3 text-xs font-medium text-[#6B6670] uppercase tracking-wide font-sans">#</th>
                      <th className="text-left p-3 text-xs font-medium text-[#6B6670] uppercase tracking-wide font-sans">Product</th>
                      <th className="text-right p-3 text-xs font-medium text-[#6B6670] uppercase tracking-wide font-sans">Revenue</th>
                      <th className="text-right p-3 text-xs font-medium text-[#6B6670] uppercase tracking-wide font-sans">Units</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.top_products.slice(0, 7).map((p: any, i: number) => {
                      const catStyle = getCategoryStyle(p.category);
                      return (
                        <tr key={p.name} className="border-b border-[#E6E2DE] last:border-0 hover:bg-[#FAF9F7] transition-colors">
                          <td className="p-3 text-sm font-sans text-[#6B6670] tabular-nums">{i + 1}</td>
                          <td className="p-3">
                            <span className="text-sm font-sans text-[#2D2A2E] font-medium">{p.name}</span>
                            <span className={`ml-2 text-[10px] font-sans font-medium px-1.5 py-0.5 rounded-full ${catStyle.bg} ${catStyle.text}`}>{p.category}</span>
                          </td>
                          <td className="p-3 text-sm font-sans text-[#2D2A2E] text-right tabular-nums">{fmt(p.revenue)}</td>
                          <td className="p-3 text-sm font-sans text-[#2D2A2E] text-right tabular-nums">{fmtCount(p.units)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <button
                onClick={() => navigate('/sales/products')}
                className="no-print flex items-center justify-center gap-1 p-3 text-sm font-sans text-[#4A2D5E] font-medium border-t border-[#E6E2DE] hover:bg-[#FAF9F7] transition-colors"
              >
                View full product scoreboard <ChevronRight size={14} strokeWidth={2} />
              </button>
            </div>
          </div>

          {/* Bundle Opportunities + Items Needing Attention */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white border border-[#E6E2DE] rounded-[14px] p-6 shadow-card">
              <div className="flex items-center gap-2 mb-4">
                <Zap size={18} strokeWidth={1.5} className="text-[#2E6B4F]" />
                <h3 className="font-serif text-base text-[#2D2A2E]">Smart Bundle Opportunities</h3>
              </div>
              {data.bundles?.length > 0 ? (
                <div className="space-y-3">
                  {data.bundles.map((b: any, i: number) => (
                    <div key={i} className="p-3 bg-[#FAF9F7] rounded-[8px]">
                      <div className="font-sans text-sm text-[#2D2A2E] font-medium">{b.pair}</div>
                      <div className="text-xs font-sans text-[#6B6670] mt-1">Purchased together {fmtPct(b.frequency)} of the time</div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm font-sans text-[#6B6670]">Not enough data for bundle recommendations yet.</p>
              )}
              <button onClick={() => navigate('/sales/insights')} className="no-print flex items-center gap-1 mt-3 text-sm font-sans text-[#4A2D5E] font-medium hover:underline">
                See all recommendations <ChevronRight size={14} strokeWidth={2} />
              </button>
            </div>

            <div className="bg-white border border-[#E6E2DE] rounded-[14px] p-6 shadow-card">
              <div className="flex items-center gap-2 mb-4">
                <AlertCircle size={18} strokeWidth={1.5} className="text-[#B54A4A]" />
                <h3 className="font-serif text-base text-[#2D2A2E]">Items Needing Attention</h3>
              </div>
              {data.declining_items?.length > 0 ? (
                <div className="space-y-2">
                  {data.declining_items.map((item: any) => (
                    <div key={item.name} className="flex items-center justify-between py-2 border-b border-[#E6E2DE] last:border-0">
                      <div className="flex items-center gap-2">
                        <Flag size={14} strokeWidth={1.5} className="text-[#B54A4A]/60" />
                        <span className="text-sm font-sans text-[#2D2A2E]">{item.name}</span>
                      </div>
                      <Change value={item.change} size="sm" />
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm font-sans text-[#6B6670]">All products performing consistently. No alerts.</p>
              )}
              <button onClick={() => navigate('/sales/insights')} className="no-print flex items-center gap-1 mt-3 text-sm font-sans text-[#4A2D5E] font-medium hover:underline">
                View all alerts <ChevronRight size={14} strokeWidth={2} />
              </button>
            </div>
          </div>
        </>
      )}
    </motion.div>
  );
}
